/**
 * The chat composer's context ring and the usage popover it opens: how full
 * this chat's context window is, then the 5-hour and weekly limits with when
 * each resets — or, when an organisation pays, the month's spend against its
 * limit — then a way to the Usage tab. The popover's shape is the one the
 * prototype copied (project/uploads/pasted-1790175466291-0.png).
 *
 * Anything used shows, however little: "<1%" rather than "0%", a dot of arc
 * and a sliver of bar.
 */

import React from 'react';
import type { ContextUse, UsageSummary } from '../../api';
import { Bar, Ph } from '../ui';
import { clampPct, pctLabel, poundsLabel, resetLine, windowLabel } from './usageText';

/** Circumference of the r=7 ring, rounded as the prototype rounds it. */
const RING = 44;

/** `Bar` for something used: a sliver the rounding hides still shows, as the
 *  track's first 2px. The sidebar and the Usage tab draw theirs with it too. */
export function UsageBar({ pct, used }: { pct: number; used: number }) {
  return (
    <Bar
      pct={pct}
      style={
        used > 0
          ? { background: 'linear-gradient(90deg, var(--primary) 2px, var(--app-panel-sunken) 2px)' }
          : undefined
      }
    />
  );
}

export function UsageRing({
  context,
  open,
  onToggle
}: {
  context: ContextUse | null;
  open: boolean;
  onToggle: () => void;
}) {
  const pct = context?.pct ?? 0;
  const tokens = context?.tokens ?? 0;
  const filled = tokens > 0 ? Math.max(1, (clampPct(pct) / 100) * RING) : 0;
  return (
    <button
      type="button"
      className="bare hov-tint"
      aria-label={`Usage · this chat ${pctLabel(pct, tokens)}`}
      title="Usage"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onToggle}
      // The popover closes on a pointer-down outside it. This control is
      // outside it, so without this a click here would close it and the
      // click's own toggle would open it again.
      onPointerDown={(e) => {
        if (open) e.stopPropagation();
      }}
      style={{
        width: 36,
        height: 36,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--radius-sm)',
        background: open ? 'var(--primary-soft)' : 'transparent'
      }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <circle cx="9" cy="9" r="7" fill="none" stroke="var(--border-strong)" strokeWidth="2.2" />
        {filled > 0 ? (
          <circle
            cx="9"
            cy="9"
            r="7"
            fill="none"
            stroke="var(--primary)"
            strokeWidth="2.2"
            strokeDasharray={`${filled.toFixed(1)} ${RING}`}
            transform="rotate(-90 9 9)"
            strokeLinecap="round"
          />
        ) : null}
      </svg>
    </button>
  );
}

const row: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 13 };
const pctText: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11.5,
  color: 'var(--text-body)',
  width: 36,
  textAlign: 'right'
};

export const UsagePopover = React.forwardRef<
  HTMLDivElement,
  { context: ContextUse | null; usage: UsageSummary; onDetails: () => void }
>(function UsagePopover({ context, usage, onDetails }, ref) {
  const contextPct = context?.pct ?? 0;
  const contextTokens = context?.tokens ?? 0;
  const limits = [
    { label: '5-hour limit', w: usage.fiveHour },
    { label: 'Weekly', w: usage.weekly }
  ];

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Usage"
      className="fade-in"
      style={{
        position: 'absolute',
        right: 0,
        bottom: 'calc(100% + 8px)',
        zIndex: 10,
        width: 340,
        maxWidth: '100%',
        boxSizing: 'border-box',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-app-card)',
        boxShadow: 'var(--shadow-lg)'
      }}
    >
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={row}>
          <span style={{ flex: 1, fontWeight: 600, color: 'var(--text-strong)' }}>This chat</span>
          <span style={pctText}>{pctLabel(contextPct, contextTokens)}</span>
        </div>
        <UsageBar pct={contextPct} used={contextTokens} />
      </div>

      <div
        style={{
          borderTop: '1px solid var(--border-subtle)',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        {usage.month ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={row}>
              <span style={{ flex: 1, fontWeight: 600, color: 'var(--text-strong)' }}>This month</span>
              <span style={{ color: 'var(--text-muted)' }}>
                {poundsLabel(usage.month.spentPence)}
                {usage.month.limitPence !== null ? ` of ${poundsLabel(usage.month.limitPence)}` : ''}
              </span>
            </div>
            <UsageBar pct={usage.month.pct ?? 0} used={usage.month.spentPence} />
          </div>
        ) : null}
        {(usage.month ? [] : limits).map(({ label, w }) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={row}>
              <span style={{ flex: 1, fontWeight: 600, color: 'var(--text-strong)' }}>{label}</span>
              <span style={{ color: 'var(--text-muted)' }}>{w.allowance === null ? '' : resetLine(w.resetsAt)}</span>
              <span style={pctText}>{windowLabel(w)}</span>
            </div>
            {w.allowance !== null ? <UsageBar pct={w.pct} used={w.used} /> : null}
          </div>
        ))}
      </div>

      <button
        type="button"
        className="bare hov-row"
        onClick={onDetails}
        style={{
          width: '100%',
          borderTop: '1px solid var(--border-subtle)',
          borderRadius: '0 0 var(--radius-app-card) var(--radius-app-card)',
          padding: '12px 16px',
          fontSize: 13,
          color: 'var(--text-body)',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <span style={{ flex: 1 }}>Usage details</span>
        <Ph name="arrow-right" color="var(--text-muted)" />
      </button>
    </div>
  );
});
